import React from 'react';

import type { GameState } from '../../types/game';
import type { GameAction } from '../gameReducer';
import { useOnlineLobby } from './useOnlineLobby';
import { useOnlineClientOrchestrator } from './useOnlineClientOrchestrator';
import { useHostTakeover } from './useHostTakeover';

type RejoinOnlineGame = (
  gameId: string,
  options?: { groupToken?: string | null; skipGroupLookup?: boolean; assumeHost?: boolean },
) => Promise<boolean>;

export type UseOnlineLobbyIdentityArgs = {
  gameState: GameState;
  dispatch: React.Dispatch<GameAction>;
  modeHint?: GameState['mode'];
  rejoinOnlineGame: RejoinOnlineGame;
  identity: {
    playerId: string | null;
    setPlayerId(value: string | null): void;
    isHost: boolean;
    setIsHost(value: boolean): void;
  };
};

export type UseOnlineLobbyIdentityResult = {
  derived: {
    isHost: boolean;
    currentClientPlayerId: string | null;
    pendingReadyChanges: ReadonlySet<string>;
  };
  lobby: ReturnType<typeof useOnlineLobby>;
  sync: {
    refreshSnapshot(reason?: string): Promise<boolean>;
  };
};

export function useOnlineLobbyIdentity({
  gameState,
  dispatch,
  modeHint,
  rejoinOnlineGame,
  identity,
}: UseOnlineLobbyIdentityArgs): UseOnlineLobbyIdentityResult {
  const lobby = useOnlineLobby({
    gameState,
    dispatch,
    setIsHost: identity.setIsHost,
    setCurrentClientPlayerId: identity.setPlayerId,
    currentClientPlayerId: identity.playerId,
  });

  const online = useOnlineClientOrchestrator({
    state: gameState,
    dispatch,
    client: {
      playerId: identity.playerId,
      setPlayerId: identity.setPlayerId,
      isHost: identity.isHost,
      setIsHost: identity.setIsHost,
    },
    joinGame: rejoinOnlineGame,
    modeHint,
    onPlayerReadyApplied: lobby.clearPendingReadyChange,
  });

  useHostTakeover({
    gameState,
    isHost: identity.isHost,
    currentClientPlayerId: identity.playerId,
    setIsHost: identity.setIsHost,
  });

  return {
    derived: {
      isHost: identity.isHost,
      currentClientPlayerId: identity.playerId,
      pendingReadyChanges: lobby.pendingReadyChanges,
    },
    lobby,
    sync: {
      refreshSnapshot: online.refreshSnapshot,
    },
  };
}
